import React, { useEffect, useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Checkbox, Row, Col, Alert, Collapse, Typography } from 'antd';
import { groupBy } from 'lodash';
import UserStore from 'stores/UserStore';
import ExtraInput from '../ExtraInput';
import ExtraFile from '../ExtraFile';
import { ButtonLinkPreviewFAI } from '@partsbadger/library';

const { Title } = Typography;
const { Panel } = Collapse;

interface IProps {
    value?: any;
    onChange: (value: string) => void;
}

interface IPropsOption {
    value: any;
    option: { id: number; field_type: string; name: string; description: string };
    checked: boolean;
    files: any;
    handleChange: (checked: boolean, optionId?: number | null, value?: any, files?: any) => void;
}

const Option = ({ option, checked, value, files, handleChange }: IPropsOption) => {
    let field_input: React.ReactNode | null = null;

    if (checked) {
        if (option.field_type === 'input') {
            field_input = (
                <ExtraInput
                    value={value}
                    handleChange={value => {
                        handleChange(checked, value, null);
                    }}
                />
            );
        } else if (option.field_type === 'file') {
            field_input = (
                <ExtraFile
                    value={files}
                    handleChange={(files: any) => {
                        handleChange(checked, null, files);
                    }}
                />
            );
        }
    }

    return (
        <div className="flex items-center gap-2">
            <Checkbox
                key={option.id}
                value={option.id}
                checked={checked}
                onChange={event => {
                    handleChange(event.target.checked, value, null);
                }}
            >
                {option.name} {field_input}
                {option.description && (
                    <div style={{ padding: '10px 0' }}>
                        <Alert showIcon icon={<InfoCircleOutlined />} message={option.description} type="error" />
                    </div>
                )}
            </Checkbox>
            <div className="pt-1">
                <ButtonLinkPreviewFAI
                    custom_requirements={[
                        {
                            key: option.id.toString(),
                            label: option.name,
                            value: value,
                            files: files,
                        },
                    ]}
                />
            </div>
        </div>
    );
};

const Options: React.FC<IProps> = ({ value, onChange }) => {
    const [requirements, setRequirements] = useState<any>([]);

    useEffect(() => {
        UserStore.requirementTypes().then(data => {
            const grouped_by = groupBy(data?.results, item => {
                return item.type ? item.type : 'Additional Requirements';
            });
            setRequirements(grouped_by);
        });
    }, []);

    const handleChange = (checked: boolean, requirement: any, val: any, files: any) => {
        let new_items = [];

        if (checked) {
            const exist = value.find((item: any) => item.requirement === requirement);

            if (exist) {
                // Replace if exits
                new_items = value.map((item: any) =>
                    item.requirement === requirement
                        ? {
                              requirement: requirement,
                              value: val,
                              files: files,
                          }
                        : item
                );
            } else {
                // Append the new one
                new_items = [
                    ...value,
                    {
                        requirement: requirement,
                        value: val,
                        files: files,
                    },
                ];
            }
        } else {
            //Delete the item
            new_items = value.filter((item: any) => item.requirement !== requirement);
        }

        onChange(new_items);
    };

    const checkbox_items = Object.keys(requirements)?.map((key, index) => {
        const options = requirements[key]?.map((option: any) => {
            const current = value.find((row: any) => parseInt(row.requirement) === option.id);

            return (
                <Col key={option.id} md={12} span={24}>
                    <Option
                        option={option}
                        checked={!!current}
                        value={current?.value}
                        files={current?.files}
                        handleChange={(checked, value, files) => {
                            handleChange(checked, option.id, value, files);
                        }}
                    />
                </Col>
            );
        });

        return (
            <Panel
                key={index}
                header={
                    <Title level={4} className="mb-0">
                        {key}
                    </Title>
                }
            >
                <Row>{options}</Row>
            </Panel>
        );
    });

    return (
        <Collapse accordion style={{ width: '100%' }} defaultActiveKey={['0']}>
            {checkbox_items}
        </Collapse>
    );
};

export default Options;
